import {
  Alert,
  Box,
  Button,
  Checkbox,
  Icons,
  iconSize,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  FormHelperText,
  useSnackbar,
} from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { FileDetail, SiteVisitStageData } from '@munifocus/contracts-gov';
import { buildRepeatVisitPrefill } from '@munifocus/contracts-gov';
import { SnippetPicker, useCaretInsert } from '../../components/SnippetPicker';
import { useVisits } from '../../lib/visits-api';
import { EntryWarrantPanel } from '../../components/EntryWarrantPanel';
import { StageLayout } from '../../components/StageLayout';
import { useSaveStage } from '../../lib/files-api';
import { useSubmitValidation } from '../../lib/useSubmitValidation';
import { proceedingsLimitationClock } from '../../lib/escalation-display';
import { frenchFirstOfMonth } from '@munifocus/utils';

// Statutory-assessment checkboxes, each carrying its discreet provision ref.
const ASSESSMENTS = [
  { key: 'assessment131_1', ref: 's. 131(1)', labelKey: 'gov.siteVisit.assess1311' },
  { key: 'assessment131_2', ref: 's. 131(2)', labelKey: 'gov.siteVisit.assess1312' },
  { key: 'assessment131_3', ref: 's. 131(3)', labelKey: 'gov.siteVisit.assess1313' },
  { key: 'assessment139', ref: 's. 139', labelKey: 'gov.siteVisit.assess139' },
] as const;

const ASSESSMENT_KEYS = [
  'assessment131_1',
  'assessment131_2',
  'assessment131_3',
  'assessment139',
] as const;

export function SiteVisitStage({ file }: { file: FileDetail }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const save = useSaveStage(file.id, 'site_visit');
  const initial = (file.stageData?.site_visit ?? {}) as SiteVisitStageData;

  const [observations, setObservations] = useState(initial.observations ?? '');
  // No default. Whether the complaint is substantiated is the finding this
  // stage exists to make; a form that arrives answering "yes" records a finding
  // nobody made whenever the officer does not think to change it.
  const [substantiated, setSubstantiated] = useState<'yes' | 'no' | ''>(
    initial.substantiated === undefined ? '' : initial.substantiated ? 'yes' : 'no',
  );
  const [accessRefused, setAccessRefused] = useState(initial.accessRefused ?? false);
  const [inspectionDate, setInspectionDate] = useState(initial.inspectionDate ?? '');
  const [offenseDate, setOffenseDate] = useState(initial.offenseDate ?? '');
  const [assessments, setAssessments] = useState<Record<string, boolean>>({
    assessment131_1: initial.assessment131_1 ?? false,
    assessment131_2: initial.assessment131_2 ?? false,
    assessment131_3: initial.assessment131_3 ?? false,
    assessment139: initial.assessment139 ?? false,
  });
  const [informalNotice, setInformalNotice] = useState(initial.informalNotice ?? false);
  const [estimatedCost, setEstimatedCost] = useState(initial.estimatedCost ?? '');
  const observationsCaret = useCaretInsert();
  const snackbar = useSnackbar();

  // "Repeat last visit": start a follow-up from what the last visit recorded.
  // Only empty fields are filled — the officer's own entries are never
  // overwritten — and dates are never carried (they are facts of this visit).
  const visits = useVisits(file.id);
  const prefill = buildRepeatVisitPrefill(visits.data, initial);
  // What the prefill would change right now — the banner shows only when this
  // is not empty, so an inspection already recorded does not offer itself back.
  const sv = prefill?.siteVisit ?? {};
  const prefillObservations = sv.observations ?? prefill?.notes ?? null;
  const plan = {
    observations: !observations.trim() && prefillObservations ? prefillObservations : null,
    substantiated:
      substantiated === '' && sv.substantiated !== undefined
        ? sv.substantiated
          ? ('yes' as const)
          : ('no' as const)
        : null,
    accessRefused: !accessRefused && sv.accessRefused === true,
    assessments: ASSESSMENT_KEYS.filter((k) => sv[k] && !assessments[k]),
    informalNotice: !informalNotice && sv.informalNotice === true,
    estimatedCost: !estimatedCost && sv.estimatedCost ? sv.estimatedCost : null,
  };
  const canPrefill =
    !!plan.observations ||
    !!plan.substantiated ||
    plan.accessRefused ||
    plan.assessments.length > 0 ||
    plan.informalNotice ||
    !!plan.estimatedCost;
  const applyPrefill = () => {
    if (plan.observations) setObservations(plan.observations);
    if (plan.substantiated) setSubstantiated(plan.substantiated);
    if (plan.accessRefused) setAccessRefused(true);
    if (plan.assessments.length) {
      setAssessments((prev) => {
        const next = { ...prev };
        for (const k of plan.assessments) next[k] = true;
        return next;
      });
    }
    if (plan.informalNotice) setInformalNotice(true);
    if (plan.estimatedCost) setEstimatedCost(plan.estimatedCost);
    snackbar.success(t('gov.repeatVisit.applied'));
  };

  // The offence date drives the six-month proceedings limitation (POPA s.21(1));
  // preview the resulting deadline so the officer sees the clock they're starting.
  const limitation = offenseDate ? proceedingsLimitationClock(offenseDate) : null;

  // A substantiated violation requires observations to back it; access refusal
  // routes to the warrant sub-flow (epic W) and shouldn't claim a finding yet.
  const v = useSubmitValidation({
    observations:
      accessRefused || observations.trim() ? null : t('gov.siteVisit.observationsRequired'),
    substantiated: accessRefused || substantiated !== '' ? null : t('common.validation.choose'),
  });

  const onComplete = () =>
    save.mutate(
      {
        observations,
        substantiated: substantiated === '' ? undefined : substantiated === 'yes',
        accessRefused,
        inspectionDate: inspectionDate || undefined,
        offenseDate: offenseDate || undefined,
        assessment131_1: assessments.assessment131_1,
        assessment131_2: assessments.assessment131_2,
        assessment131_3: assessments.assessment131_3,
        assessment139: assessments.assessment139,
        informalNotice,
        estimatedCost: estimatedCost || undefined,
        advance: !accessRefused,
      },
      { onSuccess: () => navigate(`/files/${file.id}`) },
    );

  const record = [
    { label: t('gov.siteVisit.inspectionDate'), value: inspectionDate },
    {
      label: t('gov.siteVisit.substantiated'),
      value:
        substantiated === ''
          ? ''
          : t(
              substantiated === 'yes'
                ? 'gov.siteVisit.substantiatedYes'
                : 'gov.siteVisit.substantiatedNo',
            ),
    },
    {
      label: t('gov.siteVisit.accessRefused'),
      value: accessRefused ? t('common.yes') : t('common.no'),
    },
    { label: t('gov.siteVisit.observations'), value: observations, block: true },
    { label: t('gov.siteVisit.offenseDate'), value: offenseDate },
    {
      label: t('gov.siteVisit.assessmentTitle'),
      // Only the boxes that were ticked; a list of everything with "no" beside
      // it reads as a form again.
      value:
        [
          assessments.assessment131_1 && t('gov.siteVisit.assess1311'),
          assessments.assessment131_2 && t('gov.siteVisit.assess1312'),
          assessments.assessment131_3 && t('gov.siteVisit.assess1313'),
          assessments.assessment139 && t('gov.siteVisit.assess139'),
        ]
          .filter(Boolean)
          .join('; ') || null,
      block: true,
    },
    {
      label: t('gov.siteVisit.informalNotice'),
      value: informalNotice ? t('common.yes') : t('common.no'),
    },
    { label: t('gov.siteVisit.estimatedCost'), value: estimatedCost },
  ];

  return (
    <StageLayout
      file={file}
      stageId="site_visit"
      record={record}
      onComplete={v.guard(onComplete)}
      saving={save.isPending}
    >
      {save.isError && <Alert severity="error">{t('gov.stage.saveError')}</Alert>}

      {prefill && canPrefill && (
        <Alert
          severity="info"
          icon={<Icons.Repeat size={iconSize.md} />}
          action={
            <Button color="inherit" size="small" onClick={applyPrefill}>
              {t('gov.repeatVisit.action')}
            </Button>
          }
        >
          {prefill.sourceDate
            ? t('gov.repeatVisit.hintDated', {
                date: frenchFirstOfMonth(
                  new Date(prefill.sourceDate).toLocaleDateString(
                    i18n.language.startsWith('fr') ? 'fr-CA' : 'en-CA',
                  ),
                ),
              })
            : t('gov.repeatVisit.hint')}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          fullWidth
          size="small"
          type="date"
          label={t('gov.siteVisit.inspectionDate')}
          slotProps={{ inputLabel: { shrink: true } }}
          value={inspectionDate}
          onChange={(e) => setInspectionDate(e.target.value)}
        />
        <TextField
          fullWidth
          size="small"
          type="date"
          label={t('gov.siteVisit.offenseDate')}
          slotProps={{ inputLabel: { shrink: true } }}
          value={offenseDate}
          onChange={(e) => setOffenseDate(e.target.value)}
          helperText={t('gov.siteVisit.offenseDateHelp')}
        />
      </Stack>
      {limitation && (
        <Alert severity={limitation.expired ? 'error' : 'info'} sx={{ py: 0 }}>
          {t('gov.siteVisit.limitationNote', {
            date: limitation.deadlineIso,
            days: limitation.daysRemaining,
          })}
        </Alert>
      )}

      <TextField
        fullWidth
        multiline
        minRows={4}
        label={t('gov.siteVisit.observations')}
        {...v.field('observations')}
        value={observations}
        onChange={(e) => setObservations(e.target.value)}
        {...observationsCaret.trackProps}
      />
      <Box sx={{ mt: -1 }}>
        <SnippetPicker
          context="note"
          onInsert={(text) =>
            setObservations((current) => observationsCaret.insertInto(current, text))
          }
        />
      </Box>

      {/* Statutory assessment (one or more), each with its discreet provision. */}
      <div>
        <Typography variant="overline" color="text.secondary">
          {t('gov.siteVisit.assessmentTitle')}
        </Typography>
        <Stack>
          {ASSESSMENTS.map((a) => (
            <FormControlLabel
              key={a.key}
              control={
                <Checkbox
                  checked={assessments[a.key]}
                  onChange={(e) =>
                    setAssessments((prev) => ({ ...prev, [a.key]: e.target.checked }))
                  }
                />
              }
              label={
                <span>
                  {t(a.labelKey)}{' '}
                  <Typography component="span" variant="caption" color="text.secondary">
                    {a.ref}
                  </Typography>
                </span>
              }
            />
          ))}
        </Stack>
      </div>

      <FormControl fullWidth size="small" error={Boolean(v.errors.substantiated)}>
        <InputLabel>{t('gov.siteVisit.substantiated')}</InputLabel>
        <Select
          label={t('gov.siteVisit.substantiated')}
          aria-label={t('gov.siteVisit.substantiated')}
          inputRef={v.register('substantiated')}
          value={substantiated}
          onChange={(e) => setSubstantiated(e.target.value as typeof substantiated)}
        >
          <MenuItem value="yes">{t('gov.siteVisit.substantiatedYes')}</MenuItem>
          <MenuItem value="no">{t('gov.siteVisit.substantiatedNo')}</MenuItem>
        </Select>
        {v.errors.substantiated && <FormHelperText>{v.errors.substantiated}</FormHelperText>}
      </FormControl>

      <TextField
        fullWidth
        size="small"
        label={t('gov.siteVisit.estimatedCost')}
        value={estimatedCost}
        onChange={(e) => setEstimatedCost(e.target.value)}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={informalNotice}
            onChange={(e) => setInformalNotice(e.target.checked)}
          />
        }
        label={t('gov.siteVisit.informalNotice')}
      />
      {informalNotice && <Alert severity="info">{t('gov.siteVisit.informalNoticeNote')}</Alert>}

      <FormControlLabel
        control={
          <Checkbox checked={accessRefused} onChange={(e) => setAccessRefused(e.target.checked)} />
        }
        label={t('gov.siteVisit.accessRefused')}
      />
      {accessRefused && <Alert severity="warning">{t('gov.siteVisit.accessRefusedNote')}</Alert>}
      {/* Refused access is the classic entry-warrant trigger (brief §6) — the
          sub-flow launches here and pauses the stage until it resolves. */}
      {accessRefused && <EntryWarrantPanel fileId={file.id} triggerPhase="visit" />}
    </StageLayout>
  );
}
